import { morph } from '@theme/morph';

/**
 * Re-fetches cart HTML and morphs cart contents in place.
 * Call after a third-party add-to-cart succeeds (for example LimeSpot FBT).
 *
 * Morphs only `cart-items-component` — not the header `shopify-section`.
 * Header/menu `<cart-icon>` badges are synced via `renderCartBubble` (same API
 * native `cart:update` uses). Morphing the whole section closes the drawer
 * `<dialog>`, which toggles `html[scroll-lock]` and `body` `position: fixed`,
 * so the page jumps to the top and then back (`html { scroll-behavior: smooth }`).
 *
 * @returns {Promise<void>}
 */
export const refreshCart = async () => {
  const hosts = [...document.querySelectorAll('cart-items-component')].filter(
    (host) => host instanceof HTMLElement && host.dataset.sectionId
  );

  if (hosts.length === 0) return;

  const sectionIds = [...new Set(hosts.map((host) => host.dataset.sectionId))];

  let sections;

  try {
    sections = await fetchSectionHtml(sectionIds);
  } catch (error) {
    console.error('Failed to refresh cart', error);
    return;
  }

  const openDialogs = [...document.querySelectorAll('cart-drawer-component dialog')].filter(
    (dialog) => dialog instanceof HTMLDialogElement && dialog.open
  );
  const hadScrollLock = document.documentElement.hasAttribute('scroll-lock');
  const bodyPosition = document.body.style.position;
  const bodyTop = document.body.style.top;
  const bodyWidth = document.body.style.width;

  for (const sectionId of sectionIds) {
    const html = sections[sectionId];

    if (!html) continue;

    morphCartSection(sectionId, html, { animateIcons: openDialogs.length === 0 });
  }

  restoreDrawerScrollLock({
    openDialogs,
    hadScrollLock,
    bodyPosition,
    bodyTop,
    bodyWidth,
  });
};

/**
 * Fetch section HTML via the Section Rendering API.
 *
 * Do **not** send `Accept: application/json` on a product or collection URL.
 * Shopify then returns resource JSON (`{"product":...}`) and ignores `?sections=`,
 * so morph gets no HTML and the drawer never updates. Observed on Bella Coastal
 * Decor product pages (HAR: response keys were only `product`).
 *
 * Fetch from `window.Shopify.routes.root` (fallback `/`) so the render context
 * is the store root — header/cart-drawer sections exist there regardless of the
 * current page, and language/market prefixes are preserved.
 *
 * @param {string[]} sectionIds
 * @returns {Promise<Record<string, string>>}
 */
const fetchSectionHtml = async (sectionIds) => {
  const root = window.Shopify?.routes?.root || '/';
  const url = new URL(root, window.location.origin);

  url.searchParams.set('sections', sectionIds.join(','));

  // No Accept: application/json — see note above. Section Rendering still returns JSON.
  const response = await fetch(url.toString(), {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch cart sections (${response.status})`);
  }

  const payload = await response.json();

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Cart section response was not a JSON object');
  }

  // Guard the product-JSON failure mode so it fails loudly instead of a silent no-op.
  const missing = sectionIds.filter((id) => typeof payload[id] !== 'string');
  if (missing.length > 0) {
    const keys = Object.keys(payload).join(', ') || '(none)';
    throw new Error(`Cart section HTML missing for ${missing.join(', ')}. Response keys: ${keys}`);
  }

  return /** @type {Record<string, string>} */ (payload);
};

/**
 * @param {string} sectionId
 * @param {string} html
 * @param {{ animateIcons: boolean }} options
 */
const morphCartSection = (sectionId, html, { animateIcons }) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const liveSection = document.getElementById(`shopify-section-${sectionId}`);

  morphMatchingElements(
    document.querySelectorAll(`cart-items-component[data-section-id="${sectionId}"]`),
    doc.querySelectorAll(`cart-items-component[data-section-id="${sectionId}"]`)
  );

  syncCartIconBadges(animateIcons);
  syncDrawerEmptyClass(liveSection, doc);
};

/**
 * Keep header/menu cart badges in sync without morphing `<cart-icon>` (or the
 * header section). Morphing icons from `routes.root` HTML can replace
 * product-page markup with homepage markup. `renderCartBubble` only updates
 * count, visibility, and `--has-cart`.
 *
 * Read the count from the live drawer after morph. Empty cart HTML has no
 * `[ref="cartItemCount"]`, so missing count means 0.
 *
 * @param {boolean} animate
 */
const syncCartIconBadges = (animate) => {
  const itemCountEl = document.querySelector('cart-items-component [ref="cartItemCount"]');
  const headingCountEl = document.querySelector(
    'cart-items-component .cart-drawer__heading [ref="cartBubbleCount"]'
  );

  let count = Number.parseInt(itemCountEl?.textContent?.trim() ?? '', 10);

  if (Number.isNaN(count)) {
    count = Number.parseInt(headingCountEl?.textContent?.replace(/\D/g, '') ?? '', 10);
  }

  if (Number.isNaN(count)) count = 0;

  for (const icon of document.querySelectorAll('cart-icon')) {
    if (typeof icon.renderCartBubble !== 'function') continue;

    icon.renderCartBubble(count, false, animate);
  }
};

/**
 * @param {NodeListOf<Element>} liveElements
 * @param {NodeListOf<Element>} nextElements
 */
const morphMatchingElements = (liveElements, nextElements) => {
  const count = Math.min(liveElements.length, nextElements.length);

  for (let index = 0; index < count; index++) {
    const liveElement = liveElements[index];
    const nextElement = nextElements[index];

    if (liveElement instanceof HTMLElement && nextElement instanceof HTMLElement) {
      morph(liveElement, nextElement);
    }
  }
};

/**
 * Keep the dialog node (and `open`) intact; only sync empty-state class.
 * @param {HTMLElement | null} liveSection
 * @param {Document} doc
 */
const syncDrawerEmptyClass = (liveSection, doc) => {
  if (!liveSection) return;

  const liveDialogs = liveSection.querySelectorAll('cart-drawer-component dialog');
  const nextDialogs = doc.querySelectorAll('cart-drawer-component dialog');
  const count = Math.min(liveDialogs.length, nextDialogs.length);

  for (let index = 0; index < count; index++) {
    const liveDialog = liveDialogs[index];
    const nextDialog = nextDialogs[index];

    if (!(liveDialog instanceof HTMLDialogElement) || !(nextDialog instanceof HTMLDialogElement)) {
      continue;
    }

    liveDialog.classList.toggle(
      'cart-drawer--empty',
      nextDialog.classList.contains('cart-drawer--empty')
    );
  }
};

/**
 * @param {object} state
 * @param {HTMLDialogElement[]} state.openDialogs
 * @param {boolean} state.hadScrollLock
 * @param {string} state.bodyPosition
 * @param {string} state.bodyTop
 * @param {string} state.bodyWidth
 */
const restoreDrawerScrollLock = ({
  openDialogs,
  hadScrollLock,
  bodyPosition,
  bodyTop,
  bodyWidth,
}) => {
  for (const dialog of openDialogs) {
    // showModal(), not `open = true`: the latter is show() and drops the top layer, backdrop and inert.
    if (dialog.isConnected && !dialog.open) dialog.showModal();
  }

  if (hadScrollLock) document.documentElement.setAttribute('scroll-lock', '');

  document.body.style.position = bodyPosition;
  document.body.style.top = bodyTop;
  document.body.style.width = bodyWidth;
};

window.refreshCart = refreshCart;
